import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material';
import { themeCreator } from './base';
import { StylesProvider } from '@mui/styles';
import { useTranslation } from 'react-i18next';

export const ThemeContext = React.createContext();

// Choix de l'utilisateur : 'light', 'dark' ou 'system' (par défaut), gardé
// dans le navigateur. « Système » suit le réglage de l'ordinateur en direct.
const STORAGE_KEY = 'appThemeMode';
const PREFERENCES = ['light', 'dark', 'system'];
const THEME_BY_MODE = {
  dark: 'NebulaFighterTheme',
  light: 'NebulaFighterLightTheme'
};

const readPreference = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return PREFERENCES.includes(saved) ? saved : 'system';
  } catch (e) {
    return 'system';
  }
};

const systemQuery = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

const ThemeProviderWrapper = function (props) {
  const [preference, _setPreference] = useState(readPreference);
  const [systemDark, setSystemDark] = useState(
    () => systemQuery()?.matches ?? true
  );
  const { i18n } = useTranslation();
  const mode =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    const query = systemQuery();
    if (!query) return undefined;
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // 🔥 Create theme with direction based on language
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const theme = useMemo(
    () => ({ ...themeCreator(THEME_BY_MODE[mode]), direction }),
    [mode, direction]
  );
  // Thème sombre pour les zones qui le restent dans tous les modes (en-tête).
  const darkTheme = useMemo(
    () => ({ ...themeCreator(THEME_BY_MODE.dark), direction }),
    [direction]
  );

  const setPreference = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // Stockage indisponible : le choix vaut pour la session en cours.
    }
    _setPreference(value);
  };

  const themeContext = useMemo(
    () => ({ preference, mode, setPreference, darkTheme }),
    [preference, mode, darkTheme]
  );

  useEffect(() => {
    // Pour les feuilles CSS globales qui doivent suivre le mode.
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;

    // Add RTL-specific styles
    if (i18n.language === 'ar') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [i18n.language]);

  return (
    <StylesProvider injectFirst>
      <ThemeContext.Provider value={themeContext}>
        <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
      </ThemeContext.Provider>
    </StylesProvider>
  );
};

export default ThemeProviderWrapper;
